import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Upload, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useAccounts,
  useUsers,
  useOpportunities,
  useCreateContractReview,
  useUploadContractReviewDocument,
} from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'

/**
 * Starting a review.
 *
 * Four things, three of them required: what the contract is, who it is with, and who should look
 * at it. The draft link is optional only because it sometimes arrives a moment later; without it
 * the reviewer has nothing to read, so the form says so rather than silently accepting an empty
 * review.
 *
 * The URL check mirrors the column CHECK constraint and the route. Three copies sounds excessive
 * for one rule, and each earns its place: the constraint is the one that actually holds, the
 * route turns a violation into a sentence instead of a 500, and this one answers a typo without
 * a round trip.
 */

/** Must stay in step with restrictToTypes() in routes/contractReviews.js. */
const ELIGIBLE_REVIEWERS = ['Super Admin', 'Admin', 'User', 'Orders', 'Quotes', 'RFP'] as const

const DOC_URL = /^https:\/\/((drive|docs)\.google\.com|([a-z0-9-]+\.)*docusign\.(net|com))\//

interface NewContractReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Prefilled when raising a review from an account. */
  accountId?: string
}

export function NewContractReviewModal({
  open,
  onOpenChange,
  accountId,
}: NewContractReviewModalProps) {
  const navigate = useNavigate()
  const createReview = useCreateContractReview()
  const uploadDoc = useUploadContractReviewDocument()

  const [title, setTitle] = useState('')
  const [account, setAccount] = useState(accountId ?? '')
  const [opportunity, setOpportunity] = useState('')
  const [reviewer, setReviewer] = useState('')
  const [draftUrl, setDraftUrl] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  /*
   * Set once the review exists but the attachment did not land.
   *
   * The review has to be created before a file can be attached to it, so the two cannot be one
   * atomic action. Pretending otherwise would mean either silently losing the file or leaving
   * the user to press a button that creates a second review. Instead the modal admits what
   * happened and offers the way forward.
   */
  const [raisedId, setRaisedId] = useState<string | null>(null)

  const { data: accountsData } = useAccounts({ limit: 10000 })
  const { data: usersData } = useUsers({ filter: 'active', limit: 1000 })

  /*
   * Scoped to the chosen customer, and not fetched at all until one is picked.
   *
   * An unscoped list would offer every deal in the CRM, and picking one from another customer
   * is refused by the server anyway: the review would render somebody else's deal as its own
   * and report against the wrong account. Better not to offer it than to explain it afterwards.
   */
  const { data: oppsData } = useOpportunities(
    account ? { account_id: account, limit: 500 } : undefined
  )
  const opportunities = account ? (oppsData?.data ?? []) : []
  const accounts = accountsData?.data ?? []

  /*
   * Only people who can actually open a contract review.
   *
   * `filter: 'active'` alone returned every active user, so Marketing, Support, Employee and
   * Training accounts all appeared in the picker. Naming one assigned them the review and sent
   * them a notification linking to a page their role is refused at, with no way to act and no
   * explanation. The server now restricts the same four roles, so picking one would fail anyway.
   *
   * Admins are included: they can act on any review, so they can certainly be the reviewer on
   * one. This list is the frontend route's list plus the two admin types.
   */
  const reviewers = (usersData?.data ?? []).filter((u) =>
    ELIGIBLE_REVIEWERS.includes(u.user_type as (typeof ELIGIBLE_REVIEWERS)[number])
  )

  const reset = () => {
    setTitle('')
    setAccount(accountId ?? '')
    setOpportunity('')
    setReviewer('')
    setDraftUrl('')
    setProblem(null)
    setFile(null)
    setRaisedId(null)
  }

  const openRaised = () => {
    const id = raisedId
    reset()
    onOpenChange(false)
    if (id) navigate(`/contract-reviews/${id}`)
  }

  const submit = async () => {
    setProblem(null)
    if (!title.trim()) return setProblem('Give the review a title, such as "Acme MSA renewal".')
    if (!account) return setProblem('Choose the customer this contract is with.')
    if (!reviewer) return setProblem('Choose who should review it.')
    if (draftUrl.trim() && !DOC_URL.test(draftUrl.trim())) {
      return setProblem('The draft link must be a Google Drive, Google Docs or DocuSign address.')
    }

    // A second press after the review already exists must not raise a duplicate.
    if (raisedId) return openRaised()

    let reviewId: string
    try {
      const created = await createReview.mutateAsync({
        title: title.trim(),
        account_id: account,
        opportunity_id: opportunity || null,
        reviewer_id: reviewer,
        draft_document_url: draftUrl.trim() || null,
      })
      reviewId = created.id
    } catch (err) {
      return setProblem(explainError(err, 'The review could not be raised'))
    }

    /*
     * The file is attached second, because the upload endpoint needs a review to attach to.
     * If it fails the review still exists and is perfectly usable, so this does not pretend
     * the whole thing failed: it says what happened and points at the review, where the file
     * can be attached again.
     */
    if (file) {
      try {
        await uploadDoc.mutateAsync({ reviewId, file, kind: 'draft' })
      } catch (err) {
        setRaisedId(reviewId)
        /*
         * The context sits OUTSIDE explainError, not in its fallback.
         *
         * For 400/409/422 explainError returns the server's own wording and the fallback is
         * never used, so phrasing it as a fallback meant "the review was raised" vanished in
         * exactly the case where the server had something specific to say, and the user was
         * left thinking nothing had been created.
         */
        return setProblem(
          `The review was raised, but the file could not be attached: ` +
            `${explainError(err, 'the upload failed')}. ` +
            `Open the review and try attaching it again.`
        )
      }
    }

    reset()
    onOpenChange(false)
    // Straight to the review: the next thing anyone wants is to see what they just raised.
    navigate(`/contract-reviews/${reviewId}`)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send a contract for review</DialogTitle>
          <DialogDescription>
            The reviewer is told straight away, and the thread keeps every round of changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cr-title">What is the contract?</Label>
            <Input
              id="cr-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Acme MSA renewal"
              aria-describedby="cr-title-hint"
            />
            {/* Said at the point of typing, not buried in Help. The title is the one piece of
                free text on a review that everybody can read, so this is the only place a
                negotiating position could be put in front of the whole team by accident. */}
            <p id="cr-title-hint" className="text-xs text-muted-foreground">
              Everyone with access to contract reviews can see this, along with the customer and the
              status. Keep pricing and negotiating positions out of it; they belong in the thread,
              which only you and the reviewer can read.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-account">Customer</Label>
            <RecordCombobox
              id="cr-account"
              records={accounts}
              value={account}
              onValueChange={(value) => {
                setAccount(value)
                // Cleared deliberately: a deal chosen for the previous customer would still be
                // sitting in state, and the server would reject the save with a message about
                // a field the user can no longer see a problem with.
                setOpportunity('')
              }}
              displayField="name"
              placeholder="Choose a customer"
              searchPlaceholder="Search accounts…"
              emptyMessage="No accounts match that."
              disabled={Boolean(accountId)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-opportunity">Opportunity (optional)</Label>
            <RecordCombobox
              id="cr-opportunity"
              records={opportunities}
              value={opportunity}
              onValueChange={setOpportunity}
              displayField="name"
              placeholder={account ? 'Choose a deal' : 'Choose a customer first'}
              searchPlaceholder="Search opportunities…"
              emptyMessage={
                account ? 'This customer has no open opportunities.' : 'Choose a customer first.'
              }
              disabled={!account}
            />
            <p className="text-xs text-muted-foreground">
              Links the review to a deal. Renewals and NDAs often have none, which is why this is
              optional.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-reviewer">Who should review it?</Label>
            <RecordCombobox
              id="cr-reviewer"
              records={reviewers}
              value={reviewer}
              onValueChange={setReviewer}
              displayField="name"
              placeholder="Choose a reviewer"
              searchPlaceholder="Search people…"
              emptyMessage="No active users match that."
            />
            <p className="text-xs text-muted-foreground">
              {/* Stated here rather than discovered later, because it is the one rule people
                  are surprised by: an administrator cannot sign on the reviewer's behalf. */}
              Only this person will be able to sign the final contract.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-file">The contract itself (optional)</Label>
            {/* A real file input, labelled and keyboard reachable; the button drives it so the
                control matches the rest of the form rather than a raw browser widget. */}
            <input
              id="cr-file"
              ref={fileInput}
              type="file"
              className="sr-only"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null)
                setProblem(null)
              }}
            />
            {file ? (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="break-all">{file.name}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setFile(null)
                    if (fileInput.current) fileInput.current.value = ''
                  }}
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                  Upload a scan or document
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              For a contract that arrived on paper. PDF, Word, or a photo.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cr-draft">Or link to the draft (optional)</Label>
            <Input
              id="cr-draft"
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              placeholder="https://drive.google.com/…"
              aria-describedby="cr-draft-hint"
            />
            <p id="cr-draft-hint" className="text-xs text-muted-foreground">
              Google Drive, Google Docs or DocuSign. If the contract is on paper, leave this empty
              and upload the scan on the next screen. Either way, the reviewer has nothing to read
              until one of them is there.
            </p>
          </div>

          {problem && (
            <Alert variant="destructive">
              <AlertDescription>{problem}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createReview.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={createReview.isPending || uploadDoc.isPending}>
            {raisedId
              ? 'Open the review'
              : createReview.isPending
                ? 'Sending…'
                : uploadDoc.isPending
                  ? 'Attaching…'
                  : 'Send for review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
